import aiChatService from '../services/aiChatService';
import processRawText from '../utils/messageParser';
import { findFixtureByUserText, findReplyChunks } from '../utils/testModeFixtures';
import debugLogger from '../utils/debugLogger';
import { buildSelectedEntityPayload } from '../utils/entityUtils';
import { markSelectedRadioEntity } from '../utils/radioSelectionUtils';

export const SESSION_STARTED_AT = Date.now();

let currentStreamController = null;
let currentStreamRequestId = 0;

const TEST_MODE_CHUNK_DELAY_MS = 40;

const buildUserMessage = (text, { isMarkdown = false } = {}) => ({
  role: 'user',
  text,
  isMarkdown,
  entityRefs: [],
  isStreaming: false,
});

const buildAiMessage = () => ({
  role: 'ai',
  rawText: '',
  reasoningSegments: [],
  text: '',
  suggestions: [],
  interactions: [],
  cards: [],
  entities: [],
  entityRefs: [],
  streamStatus: 'thinking',
  isReasoning: false,
  isStreaming: true,
  isWaiting: true,
});

/** Best-effort conversation_id when JSON.parse fails on SSE result (e.g. framing edge cases). */
const parseResultIdsFallback = (text) => {
  const mC = text.match(/"conversation_id"\s*:\s*"([0-9a-f-]{36})"/i);
  if (!mC?.[1]) return null;
  return { conversation_id: mC[1] };
};

const buildBackendTopic = (topic = {}) => ({
  ...(topic || {}),
  title: topic.title || '',
  toolTip: topic.tool_tip || topic.toolTip || null,
});

const extractLatestExchange = (messages = []) => {
  if (!messages.length) return { remainingMessages: [], exchangeMessages: [] };
  const lastMessage = messages[messages.length - 1];
  const previousMessage = messages[messages.length - 2];
  if (lastMessage?.role === 'ai' && ['user', 'topic-entity'].includes(previousMessage?.role)) {
    return {
      remainingMessages: messages.slice(0, -2),
      exchangeMessages: messages.slice(-2),
    };
  }
  return {
    remainingMessages: messages.slice(0, -1),
    exchangeMessages: messages.slice(-1),
  };
};

const lastTopicLastMessage = (topics) => {
  const last = topics[topics.length - 1];
  return last ? last.messages[last.messages.length - 1] : null;
};

/** Immutably updates the last message of the last topic. */
const withLastMessage = (state, updater) => {
  const topics = [...state.topics];
  const lastTopic = { ...topics[topics.length - 1] };
  const messages = [...lastTopic.messages];
  messages[messages.length - 1] = updater({ ...messages[messages.length - 1] });
  lastTopic.messages = messages;
  topics[topics.length - 1] = lastTopic;
  return topics;
};

/** Same SSE handling for send and confirm (both POST …/message/stream). */
const parseChatTopic = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const handleChatStreamEvent = (commit, { showStreamUi = true } = {}) => (event) => {
  if (showStreamUi) {
    commit('markFirstEventReceived');
    if (event.type === 'error') {
      commit('setError', (event.text && event.text.trim()) || 'Something went wrong. Please try again.');
    }
    if (event.type === 'delta_reasoning') commit('appendReasoningDelta', event.text);
    if (event.type === 'tool_status' || event.type === 'status') commit('appendStatus', event.text);
    if (event.type === 'text_delta') commit('appendTextDelta', event.text);
  }
  if (event.type === 'conversation_id') commit('setConversationId', event.text.trim());
  if (event.type === 'chat_topic') {
    const topic = parseChatTopic(event.text);
    if (topic?.title) commit('applyBackendChatTopic', topic);
  }
  if (event.type === 'result') {
    try {
      commit('setResult', JSON.parse(event.text));
    } catch {
      const partial = parseResultIdsFallback(event.text);
      if (partial) commit('setResult', partial);
    }
  }
};

const state = {
  panelOpen: false,
  panelSource: null,
  topics: [],
  conversationId: null,
  isStreaming: false,
  error: null,
  testMode: false,
  activeTopic: null,
  cancelledInputText: null,
  startRecordingRequestId: 0,
  lastBackendChatTopic: null,
};

const getters = {
  panelOpen: (s) => s.panelOpen,
  panelSource: (s) => s.panelSource,
  topics: (s) => s.topics,
  isStreaming: (s) => s.isStreaming,
  error: (s) => s.error,
  testMode: (s) => s.testMode,
  activeTopic: (s) => s.activeTopic,
  cancelledInputText: (s) => s.cancelledInputText,
  startRecordingRequestId: (s) => s.startRecordingRequestId,
  lastBackendChatTopic: (s) => s.lastBackendChatTopic,
};

const mutations = {
  openPanel: (state, payload = null) => {
    const source = payload && typeof payload === 'object' ? payload.source : payload;
    state.panelOpen = true;
    state.panelSource = source;
    if (payload?.startRecording) state.startRecordingRequestId += 1;
  },
  closePanel: (state) => { state.panelOpen = false; },
  applyBackendChatTopic: (state, rawTopic) => {
    const topic = buildBackendTopic(rawTopic);
    const topics = [...state.topics];
    const lastTopic = topics[topics.length - 1];
    if (String(lastTopic?.title || '').trim() === String(topic.title || '').trim()) return;

    state.activeTopic = { ...(state.activeTopic || {}), ...topic };
    state.lastBackendChatTopic = {
      title: topic.title,
      signal: (state.lastBackendChatTopic?.signal || 0) + 1,
    };
    if (!lastTopic) {
      state.topics = [{ title: topic.title, messages: [] }];
      return;
    }

    if (!lastTopic.title) {
      topics[topics.length - 1] = { ...lastTopic, title: topic.title };
      state.topics = topics;
      return;
    }

    const { remainingMessages, exchangeMessages } = extractLatestExchange(lastTopic.messages);
    if (remainingMessages.length) {
      topics[topics.length - 1] = { ...lastTopic, messages: remainingMessages };
    } else {
      topics.pop();
    }
    topics.push({ title: topic.title, messages: exchangeMessages });
    state.topics = topics;
  },
  appendMessage: (state, message) => {
    if (message?.role === 'topic-entity') {
      state.topics = [...state.topics, { title: '', messages: [message] }];
      return;
    }
    if (!state.topics.length) {
      state.topics = [{ title: '', messages: [message] }];
      return;
    }
    const topics = [...state.topics];
    const lastTopic = { ...topics[topics.length - 1] };
    lastTopic.messages = [...lastTopic.messages, message];
    topics[topics.length - 1] = lastTopic;
    state.topics = topics;
  },
  setStreaming: (state, value) => { state.isStreaming = value; },
  setError: (state, message) => {
    state.error = message;
    if (!state.topics.length) return;
    const topics = [...state.topics];
    const lastTopic = { ...topics[topics.length - 1] };
    const messages = [...lastTopic.messages];
    const lastMsg = messages[messages.length - 1];
    const errorMsg = { role: 'error', text: message, isStreaming: false };
    if (lastMsg?.role === 'ai' && lastMsg.isStreaming) {
      messages[messages.length - 1] = errorMsg;
    } else {
      messages.push(errorMsg);
    }
    lastTopic.messages = messages;
    topics[topics.length - 1] = lastTopic;
    state.topics = topics;
  },
  clearError: (state) => { state.error = null; },
  appendReasoningDelta: (state, delta) => {
    state.topics = withLastMessage(state, (last) => {
      const segs = [...last.reasoningSegments];
      const lastSeg = segs[segs.length - 1];
      if (lastSeg && lastSeg.type === 'text') {
        segs[segs.length - 1] = { ...lastSeg, text: lastSeg.text + delta };
      } else {
        segs.push({ type: 'text', text: delta });
      }
      return { ...last, reasoningSegments: segs, isReasoning: true };
    });
  },
  appendStatus: (state, statusText) => {
    const trimmed = (statusText && String(statusText).trim()) || '';
    state.topics = withLastMessage(state, (last) => ({ ...last, streamStatus: trimmed || null }));
  },
  appendTextDelta: (state, delta) => {
    state.topics = withLastMessage(state, (last) => {
      const rawText = last.rawText + delta;
      const { text, afterText, suggestions, cards, entities, interactions, parts } = processRawText(rawText);
      return {
        ...last,
        rawText,
        text,
        afterText,
        suggestions,
        cards,
        entities,
        interactions,
        parts,
        isReasoning: false,
        streamStatus: null,
      };
    });
    // Sync activeTopic when BE emits a new_topic interaction inline in the stream
    const lastTopic = state.topics[state.topics.length - 1];
    const lastMsg = lastTopic?.messages[lastTopic.messages.length - 1];
    const beTopicTag = lastMsg?.interactions?.find((i) => i.type === 'new_topic');
    if (beTopicTag) {
      state.activeTopic = {
        id: beTopicTag.primary_entity_uid || `be-${Date.now()}`,
        title: beTopicTag.title || '',
        trigger: 'backend_detected',
        toolTip: beTopicTag.tool_tip || null,
        primary_entity: beTopicTag.primary_entity_type ? {
          type: beTopicTag.primary_entity_type,
          uid: beTopicTag.primary_entity_uid || null,
        } : null,
      };
    }
  },
  finalizeLastMessage: (state) => {
    state.topics = withLastMessage(state, (last) => ({
      ...last,
      isStreaming: false,
      isWaiting: false,
      streamStatus: null,
    }));
  },
  markRadioSelection: (state, { messagePath, groupKey, selectedEntity } = {}) => {
    state.topics = markSelectedRadioEntity(state.topics, { messagePath, groupKey, selectedEntity });
  },
  markLastErrorRetried: (state) => {
    state.topics = withLastMessage(state, (last) => (
      last.role === 'error' ? { ...last, retried: true } : last
    ));
  },
  markFirstEventReceived: (state) => {
    if (!state.topics.length) return;
    const topics = [...state.topics];
    const lastTopic = { ...topics[topics.length - 1] };
    const messages = [...lastTopic.messages];
    const last = messages[messages.length - 1];
    if (!last || !last.isWaiting) return;
    messages[messages.length - 1] = { ...last, isWaiting: false };
    lastTopic.messages = messages;
    topics[topics.length - 1] = lastTopic;
    state.topics = topics;
  },
  setResult: (state, result) => {
    state.conversationId = result.conversation_id || state.conversationId;
    if (result.topic) {
      state.activeTopic = {
        ...(state.activeTopic || {}),
        ...result.topic,
        toolTip: result.topic.tool_tip || state.activeTopic?.toolTip || null,
      };
    }
  },
  setConversationId: (state, id) => {
    if (id) state.conversationId = id;
  },
  setTestMode: (state, value) => { state.testMode = !!value; },
  markConfirmActioned: (state, conversationId) => {
    state.topics = state.topics.map((topic) => ({
      ...topic,
      messages: topic.messages.map((msg) => (
        msg.role === 'confirm' && msg.conversationId === conversationId
          ? { ...msg, isActioned: true }
          : msg
      )),
    }));
  },
  resetConversation: (state) => {
    state.topics = [];
    state.conversationId = null;
    state.isStreaming = false;
    state.error = null;
    state.activeTopic = null;
    state.cancelledInputText = null;
    state.lastBackendChatTopic = null;
  },
  cancelStreaming: (state) => {
    const topics = [...state.topics];
    const lastTopic = topics[topics.length - 1];
    if (!lastTopic?.messages?.length) {
      state.isStreaming = false;
      state.cancelledInputText = null;
      return;
    }

    const nextTopic = { ...lastTopic };
    const messages = [...nextTopic.messages];
    const lastMsg = messages[messages.length - 1];
    const userMsg = messages[messages.length - 2];

    if (!lastMsg || lastMsg.role !== 'ai') {
      state.isStreaming = false;
      return;
    }

    state.cancelledInputText = userMsg?.text || null;

    if (lastMsg.text) {
      messages[messages.length - 1] = {
        ...lastMsg,
        isStreaming: false,
        isWaiting: false,
        streamStatus: null,
        cancelled: true,
      };
      nextTopic.messages = messages;
      topics[topics.length - 1] = nextTopic;
    } else {
      messages.splice(messages.length - 2, 2);
      if (messages.length === 0) {
        topics.splice(topics.length - 1, 1);
      } else {
        nextTopic.messages = messages;
        topics[topics.length - 1] = nextTopic;
      }
    }

    state.topics = topics;
    state.isStreaming = false;
  },
  stopStreamingSilently: (state) => {
    state.cancelledInputText = null;
    const lastTopic = state.topics[state.topics.length - 1];
    if (!lastTopic?.messages?.length) {
      state.isStreaming = false;
      return;
    }
    state.topics = withLastMessage(state, (last) => {
      if (last.role !== 'ai') return last;
      return {
        ...last,
        isStreaming: false,
        isWaiting: false,
        streamStatus: null,
        cancelled: false,
      };
    });
    state.isStreaming = false;
  },
  clearCancelledInputText: (state) => { state.cancelledInputText = null; },
};

/**
 * Replay canned text chunks through the normal stream handler, so the parser
 * and UI exercise exactly the same code path as a real SSE reply. Used only
 * when test-mode is active.
 *
 * We also `console.debug` every event we dispatch and the final parsed state,
 * so you can follow the "stream" and the post-parse Vuex state in DevTools.
 * Filter by `[AiChat test-mode]` in the Console to see the whole flow.
 */
const simulateStream = (commit, chunks, { label, getFinalMessage }) => new Promise((resolve) => {
  const onEvent = handleChatStreamEvent(commit);
  const tag = `[AiChat test-mode] ${label}`;
  const rawReply = chunks.join('');
  debugLogger.info(
    `%c${tag}%c raw LLM-like reply:\n%c${rawReply}`,
    'background:#4a90e2;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
    'color:inherit',
    'color:#888;white-space:pre-wrap',
  );
  debugLogger.groupCollapsed(`${tag} (per-chunk detail)`);
  debugLogger.debug(`${chunks.length} chunk(s), ${TEST_MODE_CHUNK_DELAY_MS}ms apart`);
  let i = 0;
  const next = () => {
    if (i >= chunks.length) {
      try {
        const finalMsg = getFinalMessage?.();
        if (finalMsg) {
          debugLogger.debug('final message state:', {
            text: finalMsg.text,
            entities: finalMsg.entities,
            interactions: finalMsg.interactions,
            suggestions: finalMsg.suggestions,
          });
        }
      } finally {
        debugLogger.groupEnd();
      }
      resolve();
      return;
    }
    const chunk = chunks[i];
    debugLogger.debug(`chunk ${i + 1}/${chunks.length} →`, chunk);
    onEvent({ type: 'text_delta', text: chunk });
    i += 1;
    setTimeout(next, TEST_MODE_CHUNK_DELAY_MS);
  };
  next();
});

const actions = {
  openPanel: ({ commit }, source) => commit('openPanel', source),
  closePanel: ({ commit }) => commit('closePanel'),
  markRadioSelection: ({ commit }, payload) => commit('markRadioSelection', payload),
  markLastErrorRetried: ({ commit }) => commit('markLastErrorRetried'),
  resetConversation: ({ commit, state: s }) => {
    if (s.isStreaming) { currentStreamController?.abort(); if (s.conversationId) aiChatService.abortStream(s.conversationId); }
    commit('resetConversation');
  },
  toggleTestMode: ({ commit, state: s }) => commit('setTestMode', !s.testMode),
  async launchWorkflow({ dispatch, commit }, launchRequest = {}) {
    const text = String(launchRequest.message || '').trim();
    if (!text) return;

    // Workflow launches always start a fresh conversation — never continue an
    // existing one (stale context would override the workflow's new behavior).
    await dispatch('resetConversation');
    commit('openPanel');
    const workflow = launchRequest.workflow && typeof launchRequest.workflow === 'object'
      ? launchRequest.workflow
      : null;

    await dispatch('sendMessage', {
      text,
      context: launchRequest.context || null,
      workflow,
      // Workflow launch messages are app-generated copy — safe to render as markdown.
      isMarkdown: true,
    });
  },
  stopStreaming: ({ commit, state: s }, { restoreInput = true } = {}) => {
    const controller = currentStreamController;
    currentStreamController = null;
    currentStreamRequestId += 1;
    controller?.abort();
    if (s.conversationId) aiChatService.abortStream(s.conversationId);
    if (s.isStreaming) commit(restoreInput ? 'cancelStreaming' : 'stopStreamingSilently');
  },
  clearCancelledInputText: ({ commit }) => commit('clearCancelledInputText'),

  async sendMessage({ commit, state: s }, messageOptions = {}) {
    const {
      text,
      context,
      topic,
      workflow,
      selectedEntities,
      topicEntity,
      isMarkdown,
    } = messageOptions;
    commit('clearError');
    const messageText = String(text || '').trim();
    const showStreamUi = Boolean(messageText);
    if (topicEntity) {
      commit('appendMessage', { role: 'topic-entity', entity: topicEntity, isStreaming: false });
    } else if (messageText) {
      commit('appendMessage', buildUserMessage(messageText, { isMarkdown: Boolean(isMarkdown) }));
    }
    if (showStreamUi) {
      commit('appendMessage', buildAiMessage());
      commit('setStreaming', true);
    }

    const fixture = s.testMode ? findFixtureByUserText(messageText) : null;
    if (fixture) {
      if (fixture.isError) {
        commit('setError', 'Something went wrong. Please try again.');
        commit('finalizeLastMessage');
        commit('setStreaming', false);
        return;
      }
      try {
        await simulateStream(commit, fixture.chunks, {
          label: `send "${messageText}"`,
          getFinalMessage: () => lastTopicLastMessage(s.topics),
        });
      } finally {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
      return;
    }

    const payload = { viewed_page: context };
    if (messageText) payload.message = messageText;
    if (s.conversationId) payload.conversation_id = s.conversationId;
    if (selectedEntities?.length) {
      payload.interaction = { entities: selectedEntities.map(buildSelectedEntityPayload) };
    }
    if (topic) {
      payload.chat_topic = topic;
    }
    if (workflow && typeof workflow === 'object') {
      payload.chat_workflow = workflow;
    }

    const streamRequestId = currentStreamRequestId + 1;
    currentStreamRequestId = streamRequestId;
    currentStreamController = new AbortController();
    const { signal } = currentStreamController;
    let aborted = false;

    try {
      await aiChatService.streamMessage(payload, handleChatStreamEvent(commit, { showStreamUi }), signal);
    } catch (err) {
      if (err.name === 'AbortError') {
        aborted = true;
        if (showStreamUi && currentStreamRequestId === streamRequestId) commit('cancelStreaming');
      } else if (showStreamUi) {
        commit('setError', 'Something went wrong. Please try again.');
      }
    } finally {
      if (currentStreamRequestId === streamRequestId) currentStreamController = null;
      if (showStreamUi && !aborted && currentStreamRequestId === streamRequestId) {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
    }
  },

  /**
   * Reply to a HITL interaction streamed by the assistant.
   *
   * Accepts `{ uid?, confirm }`. Sends `{ interaction: { uid?, confirm }, conversation_id }`.
   * `uid` is present for code-driven (parked) slots; absent for LLM-driven confirms.
   * `confirm` is a string: "yes" executes, anything else is treated as a cancel reason.
   */
  async confirmAction({ commit, state: s }, payload) {
    const { uid, confirm, userText } = payload || {};
    commit('clearError');
    if (!s.conversationId) {
      commit('setError', 'Could not process your confirmation. Wait for the reply to finish, then try again.');
      return;
    }
    if (!confirm) {
      commit('setError', 'Could not process your confirmation. Please try again.');
      return;
    }
    const showStreamUi = Boolean(userText);
    if (userText) commit('appendMessage', buildUserMessage(userText));
    if (showStreamUi) {
      commit('appendMessage', buildAiMessage());
      commit('setStreaming', true);
    }

    const replyChunks = s.testMode ? findReplyChunks(null, confirm) : null;
    if (replyChunks) {
      try {
        await simulateStream(commit, replyChunks, {
          label: `confirm → ${confirm}`,
          getFinalMessage: () => lastTopicLastMessage(s.topics),
        });
      } finally {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
      return;
    }

    const interaction = { confirm };
    if (uid) interaction.uid = uid;

    const streamRequestId = currentStreamRequestId + 1;
    currentStreamRequestId = streamRequestId;
    currentStreamController = new AbortController();
    const { signal } = currentStreamController;
    let aborted = false;

    try {
      await aiChatService.streamMessage(
        { conversation_id: s.conversationId, interaction },
        handleChatStreamEvent(commit, { showStreamUi }),
        signal,
      );
    } catch (err) {
      if (err.name === 'AbortError') {
        aborted = true;
        if (showStreamUi && currentStreamRequestId === streamRequestId) commit('cancelStreaming');
      } else if (showStreamUi) {
        commit('setError', 'Something went wrong. Please try again.');
      }
    } finally {
      if (currentStreamRequestId === streamRequestId) currentStreamController = null;
      if (showStreamUi && !aborted && currentStreamRequestId === streamRequestId) {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
